import { create } from 'zustand';
import type { PropertyGroup } from '@/ifc/IFCLoader';
import type { ModelTreeNode } from '@/ifc/modelTree';
import type { Measurement } from '@/viewer/Measurements';
import type { SectionAxis } from '@/viewer/section';
import type { Projection } from '@/viewer/projection';

export interface SectionState {
  enabled: boolean;
  axis: SectionAxis;
  flip: boolean;
  offset: number;
  min: number;
  max: number;
}

export interface LoadStatus {
  isLoading: boolean;
  message: string;
  percent: number;
}

export interface ToastItem {
  id: string;
  kind: 'info' | 'error';
  message: string;
}

export interface SelectionState {
  expressID: number | null;
  meshName: string | null;
}

export interface Stats {
  fps: number;
  idle: boolean;
  triangles: number;
  drawCalls: number;
  totalMeshes: number;
  visibleMeshes: number;
}

interface ViewerState {
  ready: boolean;
  modelName: string | null;
  meshCount: number;
  load: LoadStatus;
  toasts: ToastItem[];
  selection: SelectionState;
  properties: PropertyGroup[];
  propertiesLoading: boolean;
  stats: Stats;
  tree: ModelTreeNode | null;
  treeOpen: boolean;
  hiddenIDs: ReadonlySet<number>;
  deletedIDs: ReadonlySet<number>;
  edgesEnabled: boolean;
  lengthUnit: string | null;
  measureMode: boolean;
  measurements: Measurement[];
  section: SectionState;
  sectionOpen: boolean;
  projection: Projection;

  setReady: (v: boolean) => void;
  setModel: (name: string | null, meshCount: number) => void;
  setTree: (tree: ModelTreeNode | null) => void;
  setTreeOpen: (open: boolean) => void;
  setHiddenIDs: (ids: ReadonlySet<number>) => void;
  setDeletedIDs: (ids: ReadonlySet<number>) => void;
  setEdgesEnabled: (enabled: boolean) => void;
  setLengthUnit: (unit: string | null) => void;
  setMeasureMode: (on: boolean) => void;
  setMeasurements: (measurements: Measurement[]) => void;
  setSection: (section: SectionState) => void;
  setSectionOpen: (open: boolean) => void;
  setProjection: (projection: Projection) => void;
  setLoad: (load: Partial<LoadStatus>) => void;
  resetLoad: () => void;
  pushToast: (t: Omit<ToastItem, 'id'>) => void;
  removeToast: (id: string) => void;
  setSelection: (s: SelectionState) => void;
  setProperties: (properties: PropertyGroup[]) => void;
  setPropertiesLoading: (loading: boolean) => void;
  setStats: (stats: Stats) => void;
}

const initialLoad: LoadStatus = { isLoading: false, message: '', percent: 0 };

const initialSection: SectionState = {
  enabled: false,
  axis: 'z',
  flip: false,
  offset: 0,
  min: 0,
  max: 0,
};

const emptySelection: SelectionState = { expressID: null, meshName: null };
const emptyStats: Stats = {
  fps: 0,
  idle: true,
  triangles: 0,
  drawCalls: 0,
  totalMeshes: 0,
  visibleMeshes: 0,
};

export const useViewerStore = create<ViewerState>((set) => ({
  ready: false,
  modelName: null,
  meshCount: 0,
  load: initialLoad,
  toasts: [],
  selection: emptySelection,
  properties: [],
  propertiesLoading: false,
  stats: emptyStats,
  tree: null,
  treeOpen: true,
  hiddenIDs: new Set<number>(),
  deletedIDs: new Set<number>(),
  edgesEnabled: false,
  lengthUnit: null,
  measureMode: false,
  measurements: [],
  section: initialSection,
  sectionOpen: false,
  // a view preference, deliberately kept across model loads
  projection: 'perspective',

  setReady: (v) => set({ ready: v }),
  setModel: (modelName, meshCount) =>
    set({
      modelName,
      meshCount,
      selection: emptySelection,
      properties: [],
      tree: null,
      hiddenIDs: new Set<number>(),
      deletedIDs: new Set<number>(),
      measureMode: false,
      measurements: [],
      section: initialSection,
      sectionOpen: false,
    }),
  setTree: (tree) => set({ tree }),
  setTreeOpen: (treeOpen) => set({ treeOpen }),
  setHiddenIDs: (hiddenIDs) => set({ hiddenIDs }),
  setDeletedIDs: (deletedIDs) => set({ deletedIDs }),
  setEdgesEnabled: (edgesEnabled) => set({ edgesEnabled }),
  setLengthUnit: (lengthUnit) => set({ lengthUnit }),
  setMeasureMode: (measureMode) => set({ measureMode }),
  setMeasurements: (measurements) => set({ measurements }),
  setSection: (section) => set({ section }),
  setSectionOpen: (sectionOpen) => set({ sectionOpen }),
  setProjection: (projection) => set({ projection }),
  setLoad: (load) =>
    set((s) => ({ load: { ...s.load, ...load, isLoading: load.isLoading ?? true } })),
  resetLoad: () => set({ load: initialLoad }),
  pushToast: (t) =>
    set((s) => ({
      toasts: [...s.toasts, { ...t, id: crypto.randomUUID() }],
    })),
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  setSelection: (selection) => set({ selection }),
  setProperties: (properties) => set({ properties }),
  setPropertiesLoading: (propertiesLoading) => set({ propertiesLoading }),
  setStats: (stats) => set({ stats }),
}));
